import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { systemConfigAPI } from '../services/api';
import {
  CLIENT_FLEET_MAINTENANCE_CONFIG_KEY,
  CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY,
  SYSTEM_MAINTENANCE_MODE_CONFIG_KEY,
  SYSTEM_LOCALE_CONFIG_KEY
} from '../constants/systemConfigKeys';
import { useLocale } from '../contexts/LocaleContext';
import { formatInTimeZone } from 'date-fns-tz';
import { useToast } from '../contexts/ToastContext';
import { useMailing } from '../contexts/MailingContext';
import Button from '../components/Button';
import Input from '../components/Input';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Cog6ToothIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  InformationCircleIcon,
  ScaleIcon,
  EnvelopeIcon,
  PrinterIcon,
  TruckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  WrenchScrewdriverIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

/** System config key: require driver name/phone on first-pass tare for walk-in (ponctual) weighing. */
const WEIGHING_FORCE_DRIVER_PONCTUAL_KEY = 'weighing.forceDriverOnPonctualTare';
/** System config key: when true, blocks peseur bypass on insufficient prepaid balance. */
const WEIGHING_BLOCK_PREPAID_BYPASS_KEY = 'weighing.blockPeseurInsufficientPrepaidBypass';

/** Rows edited via dedicated cards above — omit from the generic "Configuration Groups" list. */
const SYSTEM_CONFIG_KEYS_HIDDEN_FROM_GENERIC = new Set([
  'calendar.senegalHolidays',
  'mailing.enabled',
  'mailing.smtp',
  'ticket.letterhead',
  'billing.pdf.includeWeighingDetail',
  WEIGHING_FORCE_DRIVER_PONCTUAL_KEY,
  WEIGHING_BLOCK_PREPAID_BYPASS_KEY,
  CLIENT_FLEET_MAINTENANCE_CONFIG_KEY,
  CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY,
  SYSTEM_MAINTENANCE_MODE_CONFIG_KEY,
  SYSTEM_LOCALE_CONFIG_KEY
]);

function shouldShowInGenericConfigList(config) {
  if (!config?.key) return true;
  if (SYSTEM_CONFIG_KEYS_HIDDEN_FROM_GENERIC.has(config.key)) return false;
  if (config.key.startsWith('tariffs.')) return false;
  return true;
}

const SystemConfigPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { refreshMailingStatus } = useMailing();
  const { refreshLocaleSettings } = useLocale();
  const currentYear = new Date().getFullYear();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);
  const [newConfig, setNewConfig] = useState({
    key: '',
    value: '',
    type: 'boolean',
    category: 'module',
    label: '',
    description: ''
  });
  const [holidayCalendar, setHolidayCalendar] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(true);
  const [savingHolidays, setSavingHolidays] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');

  // Tariff config state (TARIFS POIDS PUBLICS)
  const [tarifsData, setTarifsData] = useState(null);
  const [tarifsLoading, setTarifsLoading] = useState(true);
  const [savingTarifs, setSavingTarifs] = useState(false);
  const [editingTarifPoids, setEditingTarifPoids] = useState(false);
  const [editingTsOptions, setEditingTsOptions] = useState(false);
  const [editingTvaRate, setEditingTvaRate] = useState(false);
  const [tarifPoidsForm, setTarifPoidsForm] = useState({
    weightCeilingTon: '20',
    ouvrable: { minimumHt: '4499', rateHtPerTon: '153' },
    special: { minimumHt: '9000', rateHtPerTon: '302' }
  });
  const [tsOptionsForm, setTsOptionsForm] = useState([]);
  const [tvaRateForm, setTvaRateForm] = useState('0.18');

  const [ticketLetterheadLoading, setTicketLetterheadLoading] = useState(true);
  const [ticketLetterheadSaving, setTicketLetterheadSaving] = useState(false);
  const [ticketLogoUploading, setTicketLogoUploading] = useState(false);
  const [ticketLetterheadLogoUrl, setTicketLetterheadLogoUrl] = useState(null);
  const [ticketLetterheadForm, setTicketLetterheadForm] = useState({
    organizationName: '',
    address: '',
    telephone: '',
    fax: '',
    email: ''
  });

  const [billingPdfLoading, setBillingPdfLoading] = useState(true);
  const [billingPdfSaving, setBillingPdfSaving] = useState(false);
  const [billingPdfIncludeWeighing, setBillingPdfIncludeWeighing] = useState(false);

  const [weighingPonctualLoading, setWeighingPonctualLoading] = useState(true);
  const [weighingPonctualSaving, setWeighingPonctualSaving] = useState(false);
  const [weighingForceDriverOnPonctual, setWeighingForceDriverOnPonctual] = useState(true);
  const [weighingBlockPrepaidBypass, setWeighingBlockPrepaidBypass] = useState(false);
  const [clientsFleetMaintenanceAllowed, setClientsFleetMaintenanceAllowed] = useState(false);
  const [clientsPreTareRequestsAllowed, setClientsPreTareRequestsAllowed] = useState(false);

  const [systemMaintenanceLoading, setSystemMaintenanceLoading] = useState(true);
  const [systemMaintenanceSaving, setSystemMaintenanceSaving] = useState(false);
  const [systemMaintenanceEnabled, setSystemMaintenanceEnabled] = useState(false);

  const [localeLoading, setLocaleLoading] = useState(true);
  const [localeSaving, setLocaleSaving] = useState(false);
  const [localeForm, setLocaleForm] = useState({
    timeZone: 'Africa/Dakar',
    dateFormat: 'dd/MM/yyyy',
    dateTimeFormat: 'dd/MM/yyyy HH:mm',
    timeFormat: 'HH:mm'
  });
  const [localePresets, setLocalePresets] = useState({
    timeZones: ['Africa/Dakar', 'UTC'],
    dateFormats: ['dd/MM/yyyy'],
    dateTimeFormats: ['dd/MM/yyyy HH:mm'],
    timeFormats: ['HH:mm']
  });

  const [mailingLoading, setMailingLoading] = useState(true);
  const [mailingSaving, setMailingSaving] = useState(false);
  const [mailingTesting, setMailingTesting] = useState(false);
  const [mailingForm, setMailingForm] = useState({
    enabled: false,
    host: '',
    port: '587',
    secure: false,
    user: '',
    password: '',
    from: '',
    passwordSet: false
  });
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const isSectionExpanded = (sectionKey) => Boolean(expandedSections[sectionKey]);

  // Fetch all configurations
  const { execute: fetchConfigs } = useApi(
    systemConfigAPI.getAll,
    [],
    { showErrorToast: true, immediate: false }
  );

  const loadHolidayCalendar = useCallback(async () => {
    try {
      setHolidaysLoading(true);
      const response = await systemConfigAPI.getSenegalHolidays();
      const holidays = response?.data?.holidays || [];
      const currentYearHolidays = holidays.filter((item) => Number(item.date.slice(0, 4)) === currentYear);
      setHolidayCalendar([...currentYearHolidays].sort((a, b) => a.date.localeCompare(b.date)));
    } catch (error) {
      console.error('Error loading holiday calendar:', error);
      toast.error(t('systemConfig.holidays.loadError', 'Failed to load holiday calendar'));
    } finally {
      setHolidaysLoading(false);
    }
  }, [currentYear, t, toast]);

  const loadTariffs = useCallback(async () => {
    try {
      setTarifsLoading(true);
      const response = await systemConfigAPI.getTariffs();
      const data = response?.data;
      setTarifsData(data);
      if (data?.tarifPoids) {
        setTarifPoidsForm({
          weightCeilingTon: String(data.tarifPoids.weightCeilingTon ?? 20),
          ouvrable: {
            minimumHt: String(data.tarifPoids.ouvrable?.minimumHt ?? 4499),
            rateHtPerTon: String(data.tarifPoids.ouvrable?.rateHtPerTon ?? 153)
          },
          special: {
            minimumHt: String(data.tarifPoids.special?.minimumHt ?? 9000),
            rateHtPerTon: String(data.tarifPoids.special?.rateHtPerTon ?? 302)
          }
        });
      }
      if (Array.isArray(data?.tsOptions)) {
        setTsOptionsForm(data.tsOptions.map((opt) => ({ ...opt })));
      }
      if (typeof data?.tvaRate === 'number') {
        setTvaRateForm(String(data.tvaRate));
      }
    } catch (error) {
      console.error('Error loading tariff config:', error);
      toast.error(t('systemConfig.tariffs.loadError', 'Failed to load tariff configuration'));
    } finally {
      setTarifsLoading(false);
    }
  }, [t, toast]);

  const loadMailing = useCallback(async () => {
    try {
      setMailingLoading(true);
      const res = await systemConfigAPI.getMailing();
      const { enabled, smtp } = res.data || {};
      setMailingForm({
        enabled: Boolean(enabled),
        host: smtp?.host ?? '',
        port: String(smtp?.port ?? 587),
        secure: Boolean(smtp?.secure),
        user: smtp?.user ?? '',
        password: '',
        from: smtp?.from ?? '',
        passwordSet: Boolean(smtp?.passwordSet)
      });
    } catch (error) {
      console.error('Error loading mailing config:', error);
      toast.error(error.response?.data?.message || t('systemConfig.mailing.loadError', 'Failed to load mailing settings'));
    } finally {
      setMailingLoading(false);
    }
  }, [t, toast]);

  const loadLocaleSettings = useCallback(async () => {
    try {
      setLocaleLoading(true);
      const res = await systemConfigAPI.getLocale();
      const locale = res?.data?.locale;
      if (locale) {
        setLocaleForm({
          timeZone: locale.timeZone || 'Africa/Dakar',
          dateFormat: locale.dateFormat || 'dd/MM/yyyy',
          dateTimeFormat: locale.dateTimeFormat || 'dd/MM/yyyy HH:mm',
          timeFormat: locale.timeFormat || 'HH:mm'
        });
      }
      if (res?.data?.presets) {
        setLocalePresets(res.data.presets);
      }
    } catch (error) {
      console.error('Error loading locale settings:', error);
      toast.error(t('systemConfig.locale.loadError', 'Failed to load regional display settings'));
    } finally {
      setLocaleLoading(false);
    }
  }, [t, toast]);

  const formatLocalePreview = (pattern) => {
    try {
      return formatInTimeZone(new Date(), localeForm.timeZone || 'Africa/Dakar', pattern);
    } catch {
      return '—';
    }
  };

  const handleSaveLocaleSettings = async () => {
    try {
      setLocaleSaving(true);
      await systemConfigAPI.updateLocale(localeForm);
      await refreshLocaleSettings();
      toast.success(t('systemConfig.locale.saveSuccess', 'Regional display settings saved'));
    } catch (error) {
      console.error('Error saving locale settings:', error);
      toast.error(
        error.response?.data?.message || t('systemConfig.locale.saveError', 'Failed to save regional display settings')
      );
    } finally {
      setLocaleSaving(false);
    }
  };

  const loadSystemMaintenanceMode = useCallback(async () => {
    try {
      setSystemMaintenanceLoading(true);
      const res = await systemConfigAPI.getByKey(SYSTEM_MAINTENANCE_MODE_CONFIG_KEY);
      const v = res?.data?.value;
      setSystemMaintenanceEnabled(v === true || v === 'true' || v === '1');
    } catch (error) {
      if (error.response?.status === 404) {
        setSystemMaintenanceEnabled(false);
      } else {
        console.error('Error loading maintenance mode:', error);
        toast.error(t('systemConfig.maintenance.loadError', 'Failed to load maintenance mode'));
      }
    } finally {
      setSystemMaintenanceLoading(false);
    }
  }, [t, toast]);

  const handleSaveSystemMaintenanceMode = async () => {
    try {
      setSystemMaintenanceSaving(true);
      await systemConfigAPI.createOrUpdate({
        key: SYSTEM_MAINTENANCE_MODE_CONFIG_KEY,
        label: t('systemConfig.maintenance.configLabel', 'Maintenance mode'),
        description: t(
          'systemConfig.maintenance.configDescription',
          'When enabled, only administrators can sign in and use the application until this is turned off.'
        ),
        value: systemMaintenanceEnabled,
        type: 'boolean',
        category: 'setting'
      });
      toast.success(t('systemConfig.maintenance.saveSuccess', 'Maintenance setting saved'));
      await loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('systemConfig.maintenance.saveError', 'Failed to save maintenance mode'));
    } finally {
      setSystemMaintenanceSaving(false);
    }
  };

  const loadBillingPdfOptions = useCallback(async () => {
    try {
      setBillingPdfLoading(true);
      const data = await systemConfigAPI.getBillingPdfOptions();
      setBillingPdfIncludeWeighing(Boolean(data?.includeWeighingDetail));
    } catch (error) {
      console.error('Error loading billing PDF options:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.billingPdf.loadError', 'Failed to load billing PDF options')
      );
      setBillingPdfIncludeWeighing(false);
    } finally {
      setBillingPdfLoading(false);
    }
  }, [t, toast]);

  const handleSaveBillingPdfOptions = async () => {
    try {
      setBillingPdfSaving(true);
      const data = await systemConfigAPI.updateBillingPdfOptions({
        includeWeighingDetail: billingPdfIncludeWeighing
      });
      setBillingPdfIncludeWeighing(Boolean(data?.includeWeighingDetail));
      toast.success(t('systemConfig.billingPdf.saveSuccess', 'Billing PDF options saved'));
    } catch (error) {
      console.error('Error saving billing PDF options:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.billingPdf.saveError', 'Failed to save billing PDF options')
      );
    } finally {
      setBillingPdfSaving(false);
    }
  };

  const loadWeighingPonctualDriverPolicy = useCallback(async () => {
    try {
      setWeighingPonctualLoading(true);
      const [forceDriverRes, blockBypassRes, clientFleetRes, clientPreTareRes] = await Promise.allSettled([
        systemConfigAPI.getByKey(WEIGHING_FORCE_DRIVER_PONCTUAL_KEY),
        systemConfigAPI.getByKey(WEIGHING_BLOCK_PREPAID_BYPASS_KEY),
        systemConfigAPI.getByKey(CLIENT_FLEET_MAINTENANCE_CONFIG_KEY),
        systemConfigAPI.getByKey(CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY)
      ]);

      if (forceDriverRes.status === 'fulfilled') {
        setWeighingForceDriverOnPonctual(Boolean(forceDriverRes.value?.data?.value));
      } else if (forceDriverRes.reason?.response?.status === 404) {
        setWeighingForceDriverOnPonctual(true);
      } else {
        throw forceDriverRes.reason;
      }

      if (blockBypassRes.status === 'fulfilled') {
        setWeighingBlockPrepaidBypass(Boolean(blockBypassRes.value?.data?.value));
      } else if (blockBypassRes.reason?.response?.status === 404) {
        setWeighingBlockPrepaidBypass(false);
      } else {
        throw blockBypassRes.reason;
      }

      if (clientFleetRes.status === 'fulfilled') {
        setClientsFleetMaintenanceAllowed(Boolean(clientFleetRes.value?.data?.value));
      } else if (clientFleetRes.reason?.response?.status === 404) {
        setClientsFleetMaintenanceAllowed(false);
      } else {
        throw clientFleetRes.reason;
      }

      if (clientPreTareRes.status === 'fulfilled') {
        setClientsPreTareRequestsAllowed(Boolean(clientPreTareRes.value?.data?.value));
      } else if (clientPreTareRes.reason?.response?.status === 404) {
        setClientsPreTareRequestsAllowed(false);
      } else {
        throw clientPreTareRes.reason;
      }
    } catch (error) {
      console.error('Error loading weighing options:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.weighing.loadError', 'Failed to load weighing options')
      );
      setWeighingForceDriverOnPonctual(true);
      setWeighingBlockPrepaidBypass(false);
      setClientsFleetMaintenanceAllowed(false);
      setClientsPreTareRequestsAllowed(false);
    } finally {
      setWeighingPonctualLoading(false);
    }
  }, [t, toast]);

  const handleSaveWeighingPonctualDriverPolicy = async () => {
    try {
      setWeighingPonctualSaving(true);
      await systemConfigAPI.createOrUpdate({
        key: WEIGHING_FORCE_DRIVER_PONCTUAL_KEY,
        value: weighingForceDriverOnPonctual,
        type: 'boolean',
        category: 'weighing',
        label: t('systemConfig.weighing.forceDriverOnPonctualTare'),
        description: t('systemConfig.weighing.forceDriverOnPonctualTareHint')
      });
      await systemConfigAPI.createOrUpdate({
        key: WEIGHING_BLOCK_PREPAID_BYPASS_KEY,
        value: weighingBlockPrepaidBypass,
        type: 'boolean',
        category: 'weighing',
        label: t('systemConfig.weighing.blockPrepaidBypass'),
        description: t('systemConfig.weighing.blockPrepaidBypassHint')
      });
      await systemConfigAPI.createOrUpdate({
        key: CLIENT_FLEET_MAINTENANCE_CONFIG_KEY,
        value: clientsFleetMaintenanceAllowed,
        type: 'boolean',
        category: 'permissions',
        label: t(
          'systemConfig.clients.allowFleetMaintenance',
          'Allow clients to manage transporteurs, trucks, and drivers'
        ),
        description: t(
          'systemConfig.clients.allowFleetMaintenanceHint',
          'When disabled (default), clients can view but cannot create, edit, or delete transporteurs, trucks, and drivers.'
        )
      });
      await systemConfigAPI.createOrUpdate({
        key: CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY,
        value: clientsPreTareRequestsAllowed,
        type: 'boolean',
        category: 'permissions',
        label: t(
          'systemConfig.clients.allowPreTareRequests',
          'Allow eligible clients to submit pre-tare requests'
        ),
        description: t(
          'systemConfig.clients.allowPreTareRequestsHint',
          'Off by default: CREDIT and PAYING clients cannot submit pre-tare requests before truck arrival.'
        )
      });
      toast.success(t('systemConfig.weighing.saveSuccess', 'Weighing options saved'));
      loadConfigs();
    } catch (error) {
      console.error('Error saving weighing ponctual driver policy:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.weighing.saveError', 'Failed to save weighing options')
      );
    } finally {
      setWeighingPonctualSaving(false);
    }
  };

  const loadTicketLetterhead = useCallback(async () => {
    try {
      setTicketLetterheadLoading(true);
      const data = await systemConfigAPI.getTicketLetterhead();
      const lh = data?.letterhead || {};
      setTicketLetterheadForm({
        organizationName: lh.organizationName ?? '',
        address: lh.address ?? '',
        telephone: lh.telephone ?? '',
        fax: lh.fax ?? '',
        email: lh.email ?? ''
      });
      setTicketLetterheadLogoUrl(data?.logoUrl || null);
    } catch (error) {
      console.error('Error loading ticket letterhead:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.ticketLetterhead.loadError', 'Failed to load ticket letterhead settings')
      );
    } finally {
      setTicketLetterheadLoading(false);
    }
  }, [t, toast]);

  const handleSaveTicketLetterhead = async () => {
    try {
      setTicketLetterheadSaving(true);
      const data = await systemConfigAPI.updateTicketLetterhead({
        organizationName: ticketLetterheadForm.organizationName,
        address: ticketLetterheadForm.address,
        telephone: ticketLetterheadForm.telephone,
        fax: ticketLetterheadForm.fax,
        email: ticketLetterheadForm.email
      });
      setTicketLetterheadLogoUrl(data?.logoUrl || null);
      if (data?.letterhead) {
        setTicketLetterheadForm((f) => ({
          ...f,
          ...data.letterhead
        }));
      }
      toast.success(t('systemConfig.ticketLetterhead.saveSuccess', 'Ticket letterhead saved'));
    } catch (error) {
      console.error('Error saving ticket letterhead:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.ticketLetterhead.saveError', 'Failed to save ticket letterhead')
      );
    } finally {
      setTicketLetterheadSaving(false);
    }
  };

  const handleTicketLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setTicketLogoUploading(true);
      const fd = new FormData();
      fd.append('logo', file);
      const data = await systemConfigAPI.uploadTicketLetterheadLogo(fd);
      setTicketLetterheadLogoUrl(data?.logoUrl || null);
      if (data?.letterhead) {
        setTicketLetterheadForm((f) => ({ ...f, ...data.letterhead }));
      }
      toast.success(t('systemConfig.ticketLetterhead.logoUploadSuccess', 'Logo updated'));
    } catch (error) {
      console.error('Ticket logo upload error:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.ticketLetterhead.logoUploadError', 'Failed to upload logo')
      );
    } finally {
      setTicketLogoUploading(false);
    }
  };

  const handleRemoveTicketLogo = async () => {
    try {
      setTicketLogoUploading(true);
      const data = await systemConfigAPI.deleteTicketLetterheadLogo();
      setTicketLetterheadLogoUrl(data?.logoUrl ?? null);
      if (data?.letterhead) {
        setTicketLetterheadForm((f) => ({ ...f, ...data.letterhead }));
      }
      toast.success(t('systemConfig.ticketLetterhead.logoRemoved', 'Logo removed'));
    } catch (error) {
      console.error('Ticket logo delete error:', error);
      toast.error(
        error.response?.data?.message ||
          t('systemConfig.ticketLetterhead.logoRemoveError', 'Failed to remove logo')
      );
    } finally {
      setTicketLogoUploading(false);
    }
  };

  const handleSaveMailing = async () => {
    try {
      setMailingSaving(true);
      const smtp = {
        host: (mailingForm.host || '').trim(),
        port: parseInt(String(mailingForm.port), 10) || 587,
        secure: Boolean(mailingForm.secure),
        user: (mailingForm.user || '').trim(),
        from: (mailingForm.from || '').trim()
      };
      if (mailingForm.password) {
        smtp.password = mailingForm.password;
      }
      await systemConfigAPI.updateMailing({
        enabled: Boolean(mailingForm.enabled),
        smtp
      });
      toast.success(t('systemConfig.mailing.saveSuccess', 'Mailing settings saved'));
      setMailingForm((f) => ({ ...f, password: '' }));
      await loadMailing();
      await refreshMailingStatus();
    } catch (error) {
      console.error('Error saving mailing config:', error);
      toast.error(error.response?.data?.message || t('systemConfig.mailing.saveError', 'Failed to save mailing settings'));
    } finally {
      setMailingSaving(false);
    }
  };

  const handleTestMailing = async () => {
    try {
      setMailingTesting(true);
      await systemConfigAPI.testMailing();
      toast.success(t('systemConfig.mailing.testSuccess', 'SMTP connection verified'));
    } catch (error) {
      console.error('Mailing test error:', error);
      toast.error(error.response?.data?.message || t('systemConfig.mailing.testError', 'SMTP verification failed'));
    } finally {
      setMailingTesting(false);
    }
  };

  const handleInitializeTariffs = async () => {
    try {
      setSavingTarifs(true);
      await systemConfigAPI.initializeTariffs();
      toast.success(t('systemConfig.tariffs.initializeSuccess', 'Tariff configuration initialized with defaults'));
      await loadTariffs();
    } catch (error) {
      console.error('Error initializing tariffs:', error);
      toast.error(error.response?.data?.message || t('systemConfig.tariffs.initializeError', 'Failed to initialize tariff configuration'));
    } finally {
      setSavingTarifs(false);
    }
  };

  const handleSaveTarifPoids = async () => {
    try {
      setSavingTarifs(true);
      await systemConfigAPI.updateTarifPoids({
        weightCeilingTon: Number(tarifPoidsForm.weightCeilingTon),
        ouvrable: {
          minimumHt: Number(tarifPoidsForm.ouvrable.minimumHt),
          rateHtPerTon: Number(tarifPoidsForm.ouvrable.rateHtPerTon)
        },
        special: {
          minimumHt: Number(tarifPoidsForm.special.minimumHt),
          rateHtPerTon: Number(tarifPoidsForm.special.rateHtPerTon)
        }
      });
      toast.success(t('systemConfig.tariffs.tarifPoidsSaved', 'Taxes au Poids tariff saved'));
      setEditingTarifPoids(false);
      await loadTariffs();
    } catch (error) {
      console.error('Error saving Taxes au Poids:', error);
      toast.error(error.response?.data?.message || t('systemConfig.tariffs.saveError', 'Failed to save tariff'));
    } finally {
      setSavingTarifs(false);
    }
  };

  const handleSaveTsOptions = async () => {
    try {
      setSavingTarifs(true);
      await systemConfigAPI.updateTsOptions(tsOptionsForm.map((opt) => ({
        code: opt.code,
        description: opt.description,
        hourlyNetAmount: Number(opt.hourlyNetAmount)
      })));
      toast.success(t('systemConfig.tariffs.tsOptionsSaved', 'Taxes Supplementaires tariff saved'));
      setEditingTsOptions(false);
      await loadTariffs();
    } catch (error) {
      console.error('Error saving TS options:', error);
      toast.error(error.response?.data?.message || t('systemConfig.tariffs.saveError', 'Failed to save tariff'));
    } finally {
      setSavingTarifs(false);
    }
  };

  const handleSaveTvaRate = async () => {
    try {
      setSavingTarifs(true);
      await systemConfigAPI.updateTvaRate(Number(tvaRateForm));
      toast.success(t('systemConfig.tariffs.tvaSaved', 'TVA rate saved'));
      setEditingTvaRate(false);
      await loadTariffs();
    } catch (error) {
      console.error('Error saving TVA rate:', error);
      toast.error(error.response?.data?.message || t('systemConfig.tariffs.saveError', 'Failed to save TVA rate'));
    } finally {
      setSavingTarifs(false);
    }
  };

  const handleInitializeHolidays = async () => {
    try {
      setSavingHolidays(true);
      await systemConfigAPI.initializeSenegalHolidays([currentYear]);
      toast.success(t('systemConfig.holidays.initializeSuccess', 'Holiday calendar initialized'));
      await loadHolidayCalendar();
    } catch (error) {
      console.error('Error initializing holiday calendar:', error);
      toast.error(error.response?.data?.message || t('systemConfig.holidays.initializeError', 'Failed to initialize holiday calendar'));
    } finally {
      setSavingHolidays(false);
    }
  };

  const handleAddHoliday = async () => {
    const date = newHolidayDate.trim();
    const name = newHolidayName.trim() || t('systemConfig.holidays.defaultName', 'Public holiday');

    if (!date) {
      toast.error(t('systemConfig.holidays.dateRequired', 'Please provide a holiday date'));
      return;
    }

    if (Number(date.slice(0, 4)) !== currentYear) {
      toast.error(t('systemConfig.holidays.currentYearOnly', { year: currentYear, defaultValue: `Only dates from ${currentYear} are allowed` }));
      return;
    }

    if (holidayCalendar.some((item) => item.date === date)) {
      toast.error(t('systemConfig.holidays.duplicateDate', 'This date already exists in the holiday calendar'));
      return;
    }

    const updated = [...holidayCalendar, { date, name, source: 'manual' }]
      .sort((a, b) => a.date.localeCompare(b.date));
    setHolidayCalendar(updated);
    setNewHolidayDate('');
    setNewHolidayName('');
    await persistHolidayCalendar(updated, 'systemConfig.holidays.addSuccess');
  };

  const handleHolidayNameChange = (date, name) => {
    const updated = holidayCalendar.map((item) => (
      item.date === date ? { ...item, name } : item
    ));
    setHolidayCalendar(updated);
  };

  const handleHolidayDateChange = (index, date) => {
    if (!date) return;

    if (Number(date.slice(0, 4)) !== currentYear) {
      toast.error(t('systemConfig.holidays.currentYearOnly', { year: currentYear, defaultValue: `Only dates from ${currentYear} are allowed` }));
      return;
    }

    const duplicateIndex = holidayCalendar.findIndex((item, i) => item.date === date && i !== index);
    if (duplicateIndex !== -1) {
      toast.error(t('systemConfig.holidays.duplicateDate', 'This date already exists in the holiday calendar'));
      return;
    }

    const updated = holidayCalendar.map((item, i) => (
      i === index ? { ...item, date } : item
    )).sort((a, b) => a.date.localeCompare(b.date));

    setHolidayCalendar(updated);
  };

  const persistHolidayCalendar = async (nextCalendar, successMessageKey = 'systemConfig.holidays.saveSuccess') => {
    try {
      setSavingHolidays(true);
      const payload = nextCalendar.map((item) => ({
        date: item.date,
        name: item.name,
        source: item.source || 'manual'
      })).filter((item) => Number(item.date.slice(0, 4)) === currentYear);

      await systemConfigAPI.updateSenegalHolidays(payload);
      toast.success(t(successMessageKey, 'Holiday calendar updated successfully'));
      await loadHolidayCalendar();
    } catch (error) {
      console.error('Error saving holiday calendar:', error);
      toast.error(error.response?.data?.message || t('systemConfig.holidays.saveError', 'Failed to save holiday calendar'));
    } finally {
      setSavingHolidays(false);
    }
  };

  const handleRemoveHoliday = async (index) => {
    const updated = holidayCalendar.filter((_, i) => i !== index);
    setHolidayCalendar(updated);
    await persistHolidayCalendar(updated, 'systemConfig.holidays.deleteSuccess');
  };

  const handleSaveHolidayCalendar = async () => {
    await persistHolidayCalendar(holidayCalendar, 'systemConfig.holidays.saveSuccess');
  };

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchConfigs();
      if (response) {
        setConfigs(response.configs || []);
      }
    } catch (error) {
      console.error('Error loading configs:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchConfigs]);

  useEffect(() => {
    loadConfigs();
    loadHolidayCalendar();
    loadTariffs();
    loadMailing();
    loadSystemMaintenanceMode();
    loadLocaleSettings();
    loadBillingPdfOptions();
    loadWeighingPonctualDriverPolicy();
    loadTicketLetterhead();
  }, [
    loadConfigs,
    loadHolidayCalendar,
    loadTariffs,
    loadMailing,
    loadSystemMaintenanceMode,
    loadLocaleSettings,
    loadBillingPdfOptions,
    loadWeighingPonctualDriverPolicy,
    loadTicketLetterhead
  ]);

  const handleEdit = (config) => {
    setEditingKey(config.key);
    setEditForm({
      value: config.value,
      type: config.type,
      category: config.category || '',
      label: config.label || '',
      description: config.description || ''
    });
  };

  const handleSave = async (key) => {
    try {
      await systemConfigAPI.update(key, editForm);
      toast.success(t('systemConfig.saveSuccess'));
      setEditingKey(null);
      setEditForm({});
      loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('systemConfig.saveError'));
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditForm({});
  };

  const handleToggle = async (config) => {
    if (togglingKey === config.key) {
      return; // Prevent double-clicks
    }
    
    try {
      setTogglingKey(config.key);
      
      // Handle both boolean and string values
      const currentValue = config.value === true || config.value === 'true' || config.value === '1';
      const newValue = !currentValue;
      
      const response = await systemConfigAPI.update(config.key, { value: newValue });
      
      if (response && response.data) {
        toast.success(t('systemConfig.updateSuccess'));
        // Reload configs to get updated data
        await loadConfigs();
      } else {
        throw new Error('No response from server');
      }
    } catch (error) {
      console.error('Toggle error:', error);
      const errorMessage = error.response?.data?.message || error.message || t('systemConfig.updateError');
      toast.error(errorMessage);
    } finally {
      setTogglingKey(null);
    }
  };

  const handleAdd = async () => {
    try {
      if (!newConfig.key || !newConfig.label) {
        toast.error(t('systemConfig.keyAndLabelRequired'));
        return;
      }

      await systemConfigAPI.createOrUpdate(newConfig);
      toast.success(t('systemConfig.createSuccess'));
      setShowAddModal(false);
      setNewConfig({
        key: '',
        value: '',
        type: 'boolean',
        category: 'module',
        label: '',
        description: ''
      });
      loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('systemConfig.createError'));
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(t('systemConfig.confirmDelete'))) {
      return;
    }

    try {
      await systemConfigAPI.delete(key);
      toast.success(t('systemConfig.deleteSuccess'));
      loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('systemConfig.deleteError'));
    }
  };

  // Group configs by category (exclude keys already managed by holidays / mailing / tariffs cards)
  const groupedConfigs = configs
    .filter(shouldShowInGenericConfigList)
    .reduce((acc, config) => {
      const category = config.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(config);
      return acc;
    }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <Cog6ToothIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('systemConfig.title')}</h1>
            <p className="text-gray-600">{t('systemConfig.subtitle')}</p>
          </div>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          {t('systemConfig.addConfig')}
        </Button>
      </div>

      {/* Global maintenance lock */}
      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('maintenance')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <div className="flex items-center space-x-2">
              <WrenchScrewdriverIcon className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemConfig.maintenance.title', 'Maintenance mode')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t(
                'systemConfig.maintenance.subtitle',
                'Block all users except administrators from signing in and from using the API until maintenance is disabled.'
              )}
            </p>
          </div>
          {isSectionExpanded('maintenance') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>
        {isSectionExpanded('maintenance') && (
          <>
            <div className="mb-4">
              <Button onClick={handleSaveSystemMaintenanceMode} loading={systemMaintenanceSaving} disabled={systemMaintenanceLoading}>
                {t('common.save', 'Save')}
              </Button>
            </div>
            {systemMaintenanceLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-3 max-w-2xl">
                <label className="flex items-center gap-3 cursor-pointer rounded-full border border-amber-200 bg-amber-50/80 px-4 py-2">
                  <input
                    type="checkbox"
                    className="checkbox-app"
                    checked={systemMaintenanceEnabled}
                    onChange={(e) => setSystemMaintenanceEnabled(e.target.checked)}
                  />
                  <span className="text-sm text-gray-800 flex-1">
                    {t('systemConfig.maintenance.enableLock', 'Lock application (maintenance)')}
                  </span>
                  <span className={`text-xs font-medium ${systemMaintenanceEnabled ? 'text-amber-800' : 'text-gray-500'}`}>
                    {systemMaintenanceEnabled ? t('systemConfig.enabled') : t('systemConfig.disabled')}
                  </span>
                </label>
                <p className="text-xs text-gray-600 px-2 flex items-start gap-1">
                  <InformationCircleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                  {t(
                    'systemConfig.maintenance.adminOnlyHint',
                    'Administrator accounts can still sign in and open this page to turn maintenance off. Public operator registration is blocked while maintenance is on.'
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('locale')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemConfig.locale.title', 'Timezone & date formats')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t(
                'systemConfig.locale.subtitle',
                'Configure how dates and times are displayed across the application for all users.'
              )}
            </p>
          </div>
          {isSectionExpanded('locale') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>
        {isSectionExpanded('locale') && (
          <>
            <div className="mb-4 mt-4">
              <Button onClick={handleSaveLocaleSettings} loading={localeSaving} disabled={localeLoading}>
                {t('common.save', 'Save')}
              </Button>
            </div>
            {localeLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('systemConfig.locale.timeZone', 'Time zone')}
                  </label>
                  <select
                    value={localeForm.timeZone}
                    onChange={(e) => setLocaleForm((prev) => ({ ...prev, timeZone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {localePresets.timeZones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('systemConfig.locale.dateFormat', 'Date format')}
                  </label>
                  <select
                    value={localeForm.dateFormat}
                    onChange={(e) => setLocaleForm((prev) => ({ ...prev, dateFormat: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {localePresets.dateFormats.map((fmt) => (
                      <option key={fmt} value={fmt}>
                        {fmt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('systemConfig.locale.dateTimeFormat', 'Date & time format')}
                  </label>
                  <select
                    value={localeForm.dateTimeFormat}
                    onChange={(e) => setLocaleForm((prev) => ({ ...prev, dateTimeFormat: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {localePresets.dateTimeFormats.map((fmt) => (
                      <option key={fmt} value={fmt}>
                        {fmt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('systemConfig.locale.timeFormat', 'Time format')}
                  </label>
                  <select
                    value={localeForm.timeFormat}
                    onChange={(e) => setLocaleForm((prev) => ({ ...prev, timeFormat: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {localePresets.timeFormats.map((fmt) => (
                      <option key={fmt} value={fmt}>
                        {fmt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-gray-800">
                  <p className="font-medium text-gray-900 mb-2">
                    {t('systemConfig.locale.preview', 'Preview (now)')}
                  </p>
                  <ul className="space-y-1">
                    <li>
                      {t('systemConfig.locale.previewDate', 'Date')}:{' '}
                      <span className="font-mono">{formatLocalePreview(localeForm.dateFormat)}</span>
                    </li>
                    <li>
                      {t('systemConfig.locale.previewDateTime', 'Date & time')}:{' '}
                      <span className="font-mono">{formatLocalePreview(localeForm.dateTimeFormat)}</span>
                    </li>
                    <li>
                      {t('systemConfig.locale.previewTime', 'Time')}:{' '}
                      <span className="font-mono">{formatLocalePreview(localeForm.timeFormat)}</span>
                    </li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-2">
                    {t(
                      'systemConfig.locale.previewHint',
                      'Lists, tickets, and detail screens use these settings after save.'
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('holidays')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('systemConfig.holidays.title')}
            </h2>
            <p className="text-sm text-gray-600">
              {t('systemConfig.holidays.subtitle')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('systemConfig.holidays.currentYearNotice', { year: currentYear, defaultValue: `Managing holidays for ${currentYear}` })}
            </p>
          </div>
          {isSectionExpanded('holidays') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>

        {isSectionExpanded('holidays') && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-4">
              <Button
                variant="outline"
                onClick={handleInitializeHolidays}
                loading={savingHolidays}
                disabled={savingHolidays}
              >
                {t('systemConfig.holidays.initializeDefaults')}
              </Button>
              <Button
                onClick={handleSaveHolidayCalendar}
                loading={savingHolidays}
                disabled={savingHolidays || holidaysLoading}
              >
                {t('systemConfig.holidays.saveCalendar')}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <Input
                label={t('systemConfig.holidays.date')}
                type="date"
                value={newHolidayDate}
                min={`${currentYear}-01-01`}
                max={`${currentYear}-12-31`}
                onChange={(e) => setNewHolidayDate(e.target.value)}
              />
              <Input
                label={t('systemConfig.holidays.name')}
                value={newHolidayName}
                onChange={(e) => setNewHolidayName(e.target.value)}
                placeholder={t('systemConfig.holidays.namePlaceholder')}
              />
              <div className="flex items-end">
                <Button variant="success" onClick={handleAddHoliday} className="w-full">
                  {t('systemConfig.holidays.addDate')}
                </Button>
              </div>
            </div>

            {holidaysLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : holidayCalendar.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">
                {t('systemConfig.holidays.empty')}
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {holidayCalendar.map((holiday, index) => (
                  <div key={`${holiday.date}-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                    <div className="md:col-span-3">
                      <Input
                        type="date"
                        value={holiday.date}
                        min={`${currentYear}-01-01`}
                        max={`${currentYear}-12-31`}
                        onChange={(e) => handleHolidayDateChange(index, e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-7">
                      <Input
                        value={holiday.name}
                        onChange={(e) => handleHolidayNameChange(holiday.date, e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveHoliday(index)}
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mailing (SMTP) */}
      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('mailing')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <div className="flex items-center space-x-2">
              <EnvelopeIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemConfig.mailing.title', 'Email (SMTP)')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t('systemConfig.mailing.subtitle', 'Enable outbound email and configure the SMTP server used for bills, tickets, and other notifications.')}
            </p>
            {(() => {
              const ionosHintText = t('systemConfig.mailing.ionosHint');
              const envHintText = t(
                'systemConfig.mailing.envHint',
                'Optional env on the server: EMAIL_HOST, SMTP_SUBMISSION_HOST, EMAIL_PORT, EMAIL_USER, EMAIL_PASS; if SMTP to the public IP times out from this host but mail runs on the same machine, use host 127.0.0.1 and set SMTP_TLS_SERVERNAME=mail.yourdomain.com. Also SMTP_FORCE_IPV4=1, SMTP_CONNECTION_TIMEOUT_MS. Run `npm run smtp:probe` in the server folder from the app machine for diagnostics.'
              );
              const showIonos = String(ionosHintText || '').trim().length > 0;
              const showEnv = String(envHintText || '').trim().length > 0;
              return (
                <>
                  {showIonos ? (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5 mt-2">
                      {ionosHintText}
                    </p>
                  ) : null}
                  {showEnv ? (
                    <p className="text-xs text-gray-500 mt-2 flex items-start gap-1">
                      <InformationCircleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                      {envHintText}
                    </p>
                  ) : null}
                </>
              );
            })()}
          </div>
          {isSectionExpanded('mailing') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>

        {isSectionExpanded('mailing') && (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button variant="outline" onClick={handleTestMailing} loading={mailingTesting} disabled={mailingLoading || mailingSaving}>
                {t('systemConfig.mailing.testSmtp', 'Test SMTP')}
              </Button>
              <Button onClick={handleSaveMailing} loading={mailingSaving} disabled={mailingLoading || mailingTesting}>
                {t('common.save', 'Save')}
              </Button>
            </div>
            {mailingLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox-app"
                checked={mailingForm.enabled}
                onChange={(e) => setMailingForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
              <span className="text-sm font-medium text-gray-900">{t('systemConfig.mailing.enableLabel', 'Enable mailing in the application')}</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('systemConfig.mailing.smtpHost', 'SMTP host')}
                value={mailingForm.host}
                onChange={(e) => setMailingForm((f) => ({ ...f, host: e.target.value }))}
                placeholder="smtp.example.com"
              />
              <Input
                label={t('systemConfig.mailing.smtpPort', 'Port')}
                type="number"
                numericMode="integer"
                value={mailingForm.port}
                onChange={(e) => {
                  const port = e.target.value;
                  const n = parseInt(port, 10);
                  setMailingForm((f) => {
                    const next = { ...f, port };
                    if (n === 587) next.secure = false;
                    if (n === 465) next.secure = true;
                    return next;
                  });
                }}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox-app"
                checked={mailingForm.secure}
                onChange={(e) => setMailingForm((f) => ({ ...f, secure: e.target.checked }))}
              />
              <span className="text-sm text-gray-800">{t('systemConfig.mailing.secureTls', 'Use TLS (secure)')}</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('systemConfig.mailing.smtpUser', 'SMTP username')}
                value={mailingForm.user}
                onChange={(e) => setMailingForm((f) => ({ ...f, user: e.target.value }))}
                autoComplete="off"
              />
              <div>
                <Input
                  label={t('systemConfig.mailing.smtpPassword', 'SMTP password')}
                  type="password"
                  value={mailingForm.password}
                  onChange={(e) => setMailingForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder={mailingForm.passwordSet ? t('systemConfig.mailing.passwordPlaceholder', 'Leave blank to keep current password') : ''}
                  autoComplete="new-password"
                />
                {mailingForm.passwordSet && (
                  <p className="text-xs text-gray-500 mt-1">{t('systemConfig.mailing.passwordSetHint', 'A password is already stored.')}</p>
                )}
              </div>
            </div>

            <Input
              label={t('systemConfig.mailing.fromAddress', 'From address')}
              type="email"
              value={mailingForm.from}
              onChange={(e) => setMailingForm((f) => ({ ...f, from: e.target.value }))}
              placeholder={t('systemConfig.mailing.fromPlaceholder')}
            />
              </div>
            )}
          </>
        )}
      </div>

      {/* Ticket de pesage — en-tête (PDF) */}
      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('ticketLetterhead')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <div className="flex items-center space-x-2">
              <PrinterIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemConfig.ticketLetterhead.title', 'Printed load ticket — letterhead')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t(
                'systemConfig.ticketLetterhead.subtitle',
                'Organization name, address, phone, fax, and email appear at the top of the weighing ticket PDF (loads). Optional logo on the left.'
              )}
            </p>
          </div>
          {isSectionExpanded('ticketLetterhead') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>

        {isSectionExpanded('ticketLetterhead') && (
          <>
            <div className="mb-4">
              <Button onClick={handleSaveTicketLetterhead} loading={ticketLetterheadSaving} disabled={ticketLetterheadLoading}>
                {t('common.save', 'Save')}
              </Button>
            </div>
            {ticketLetterheadLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('systemConfig.ticketLetterhead.organizationName', 'Organization name')}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-h-[72px]"
                value={ticketLetterheadForm.organizationName}
                onChange={(e) =>
                  setTicketLetterheadForm((f) => ({ ...f, organizationName: e.target.value }))
                }
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('systemConfig.ticketLetterhead.address', 'Address')}
                value={ticketLetterheadForm.address}
                onChange={(e) => setTicketLetterheadForm((f) => ({ ...f, address: e.target.value }))}
              />
              <Input
                label={t('systemConfig.ticketLetterhead.telephone', 'Telephone')}
                value={ticketLetterheadForm.telephone}
                onChange={(e) => setTicketLetterheadForm((f) => ({ ...f, telephone: e.target.value }))}
              />
              <Input
                label={t('systemConfig.ticketLetterhead.fax', 'Fax')}
                value={ticketLetterheadForm.fax}
                onChange={(e) => setTicketLetterheadForm((f) => ({ ...f, fax: e.target.value }))}
              />
              <Input
                label={t('systemConfig.ticketLetterhead.email', 'E-mail')}
                value={ticketLetterheadForm.email}
                onChange={(e) => setTicketLetterheadForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-800 mb-2">
                {t('systemConfig.ticketLetterhead.logoLabel', 'Logo (optional)')}
              </p>
              <p className="text-xs text-gray-500 mb-2">
                {t('systemConfig.ticketLetterhead.logoHint', 'PNG or JPEG, max 2 MB. Shown on the left of the header on the ticket.')}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  id="ticket-letterhead-logo-input"
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="sr-only"
                  disabled={ticketLogoUploading || ticketLetterheadSaving}
                  onChange={handleTicketLogoChange}
                />
                <label
                  htmlFor="ticket-letterhead-logo-input"
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white cursor-pointer hover:bg-gray-50 inline-block"
                >
                  {t('systemConfig.ticketLetterhead.chooseLogo', 'Choose image…')}
                </label>
                {ticketLetterheadLogoUrl ? (
                  <>
                    <img
                      src={ticketLetterheadLogoUrl}
                      alt=""
                      className="h-14 w-14 object-contain border border-gray-200 rounded bg-white p-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveTicketLogo}
                      loading={ticketLogoUploading}
                      disabled={ticketLetterheadSaving}
                    >
                      {t('systemConfig.ticketLetterhead.removeLogo', 'Remove logo')}
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pesage — client ponctuel (chauffeur à la tare) */}
      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('weighing')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <div className="flex items-center space-x-2">
              <TruckIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemConfig.weighing.title', 'Weighing — walk-in (ponctual)')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t(
                'systemConfig.weighing.subtitle',
                'Whether the weighmaster must enter the driver name and phone on the first-pass tare for a walk-in (ponctual, no-DDP) client. Values are saved on the truck.'
              )}
            </p>
          </div>
          {isSectionExpanded('weighing') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>
        {isSectionExpanded('weighing') && (
          <>
            <div className="mb-4">
              <Button
                onClick={handleSaveWeighingPonctualDriverPolicy}
                loading={weighingPonctualSaving}
                disabled={weighingPonctualLoading}
              >
                {t('common.save', 'Save')}
              </Button>
            </div>
            {weighingPonctualLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-3 max-w-2xl">
                <div>
                  <label className="flex items-center gap-3 cursor-pointer rounded-full border border-gray-200 bg-white px-4 py-2">
                    <input
                      type="checkbox"
                      className="checkbox-app"
                      checked={weighingForceDriverOnPonctual}
                      onChange={(e) => setWeighingForceDriverOnPonctual(e.target.checked)}
                    />
                    <span className="text-sm text-gray-800 flex-1">
                      {t(
                        'systemConfig.weighing.forceDriverOnPonctualTare',
                        'Require driver name and telephone on ponctual tare (first pass)'
                      )}
                    </span>
                    <span className={`text-xs font-medium ${weighingForceDriverOnPonctual ? 'text-green-700' : 'text-gray-500'}`}>
                      {weighingForceDriverOnPonctual ? t('systemConfig.enabled') : t('systemConfig.disabled')}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1.5 px-2">
                    {t(
                      'systemConfig.weighing.forceDriverOnPonctualTareHint',
                      'On by default: both fields are mandatory. When turned off, both are optional; if left empty, generic placeholders are stored until updated.'
                    )}
                  </p>
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer rounded-full border border-gray-200 bg-white px-4 py-2">
                    <input
                      type="checkbox"
                      className="checkbox-app"
                      checked={weighingBlockPrepaidBypass}
                      onChange={(e) => setWeighingBlockPrepaidBypass(e.target.checked)}
                    />
                    <span className="text-sm text-gray-800 flex-1">
                      {t(
                        'systemConfig.weighing.blockPrepaidBypass',
                        'Block weighmaster bypass when prepaid balance is insufficient'
                      )}
                    </span>
                    <span className={`text-xs font-medium ${weighingBlockPrepaidBypass ? 'text-green-700' : 'text-gray-500'}`}>
                      {weighingBlockPrepaidBypass ? t('systemConfig.enabled') : t('systemConfig.disabled')}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1.5 px-2">
                    {t(
                      'systemConfig.weighing.blockPrepaidBypassHint',
                      'Off by default: weighmasters can continue after a confirmation when the prepaid balance is insufficient. Turn on to enforce a hard block.'
                    )}
                  </p>
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer rounded-full border border-gray-200 bg-white px-4 py-2">
                    <input
                      type="checkbox"
                      className="checkbox-app"
                      checked={clientsFleetMaintenanceAllowed}
                      onChange={(e) => setClientsFleetMaintenanceAllowed(e.target.checked)}
                    />
                    <span className="text-sm text-gray-800 flex-1">
                      {t(
                        'systemConfig.clients.allowFleetMaintenance',
                        'Allow clients to manage transporteurs, trucks, and drivers'
                      )}
                    </span>
                    <span className={`text-xs font-medium ${clientsFleetMaintenanceAllowed ? 'text-green-700' : 'text-gray-500'}`}>
                      {clientsFleetMaintenanceAllowed ? t('systemConfig.enabled') : t('systemConfig.disabled')}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1.5 px-2">
                    {t(
                      'systemConfig.clients.allowFleetMaintenanceHint',
                      'Off by default: clients can view but cannot create, edit, or delete transporteurs, trucks, and drivers.'
                    )}
                  </p>
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer rounded-full border border-gray-200 bg-white px-4 py-2">
                    <input
                      type="checkbox"
                      className="checkbox-app"
                      checked={clientsPreTareRequestsAllowed}
                      onChange={(e) => setClientsPreTareRequestsAllowed(e.target.checked)}
                    />
                    <span className="text-sm text-gray-800 flex-1">
                      {t(
                        'systemConfig.clients.allowPreTareRequests',
                        'Allow eligible clients to submit pre-tare requests'
                      )}
                    </span>
                    <span className={`text-xs font-medium ${clientsPreTareRequestsAllowed ? 'text-green-700' : 'text-gray-500'}`}>
                      {clientsPreTareRequestsAllowed ? t('systemConfig.enabled') : t('systemConfig.disabled')}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1.5 px-2">
                    {t(
                      'systemConfig.clients.allowPreTareRequestsHint',
                      'Off by default: CREDIT and PAYING clients cannot submit pre-tare requests before truck arrival.'
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Facture PDF (pesage) */}
      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('billingPdf')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <div className="flex items-center space-x-2">
              <PrinterIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemConfig.billingPdf.title', 'Weighing bill PDF')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t(
                'systemConfig.billingPdf.subtitle',
                'Optional block on printed and emailed weighing invoices: tare and gross weights with date and time (same layout as the load ticket). Off by default.'
              )}
            </p>
          </div>
          {isSectionExpanded('billingPdf') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>
        {isSectionExpanded('billingPdf') && (
          <>
            <div className="mb-4">
              <Button onClick={handleSaveBillingPdfOptions} loading={billingPdfSaving} disabled={billingPdfLoading}>
                {t('common.save', 'Save')}
              </Button>
            </div>
            {billingPdfLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <label className="flex items-start gap-3 cursor-pointer max-w-2xl">
                <input
                  type="checkbox"
                  className="checkbox-app mt-0.5"
                  checked={billingPdfIncludeWeighing}
                  onChange={(e) => setBillingPdfIncludeWeighing(e.target.checked)}
                />
                <span className="text-sm text-gray-800">
                  {t(
                    'systemConfig.billingPdf.includeWeighingDetail',
                    'Include the weighing detail section (tare / brut with date and time) on bill PDFs'
                  )}
                </span>
              </label>
            )}
          </>
        )}
      </div>

      {/* Tarifs Poids Publics */}
      <div className="card">
        <button
          type="button"
          onClick={() => toggleSection('tariffs')}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div>
            <div className="flex items-center space-x-2">
              <ScaleIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemConfig.tariffs.title', 'Tarifs Poids Publics')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t('systemConfig.tariffs.subtitle', 'Tarifs utilisés pour le calcul des Taxes au Poids et des Taxes Supplémentaires (TS). Source: TARIFS POIDS PUBLICS.')}
            </p>
          </div>
          {isSectionExpanded('tariffs') ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 mt-1 shrink-0" />
          )}
        </button>

        {isSectionExpanded('tariffs') && (
          <>
            <div className="mb-4">
              <Button
                variant="outline"
                onClick={handleInitializeTariffs}
                loading={savingTarifs}
                disabled={savingTarifs}
              >
                {t('systemConfig.tariffs.initializeDefaults', 'Initialiser les valeurs par défaut')}
              </Button>
            </div>
            {tarifsLoading ? (
              <div className="py-6 flex justify-center"><LoadingSpinner /></div>
            ) : (
              <div className="space-y-6">

            {/* Taxes au Poids */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t('systemConfig.tariffs.tarifPoidsTitle', 'Taxes au Poids (HT)')}
                </h3>
                {!editingTarifPoids && (
                  <Button variant="outline" size="sm" onClick={() => setEditingTarifPoids(true)} disabled={savingTarifs}>
                    <PencilIcon className="h-4 w-4 mr-1" />
                    {t('common.edit')}
                  </Button>
                )}
              </div>
              <div className="mb-3 flex items-center gap-3">
                <span className="text-sm text-gray-700">
                  {t('systemConfig.tariffs.weightCeilingTon', 'Plafond de poids (tonnes) pour appliquer le minimum HT')}
                </span>
                {editingTarifPoids ? (
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={tarifPoidsForm.weightCeilingTon}
                    onChange={(e) => setTarifPoidsForm((f) => ({ ...f, weightCeilingTon: e.target.value }))}
                    className="w-28"
                  />
                ) : (
                  <span className="font-mono text-sm text-gray-900">
                    {tarifsData?.tarifPoids?.weightCeilingTon ?? 20}
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">{t('systemConfig.tariffs.period', 'Période')}</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-700">{t('systemConfig.tariffs.minimumHt', 'Minimum HT (FCFA)')}</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-700">{t('systemConfig.tariffs.rateHtPerTon', 'Tarif/tonne HT (FCFA)')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-gray-700">{t('systemConfig.tariffs.ouvrable', 'Jours ouvrables (Lun–Ven, 8h–16h)')}</td>
                      <td className="px-4 py-3 text-right">
                        {editingTarifPoids ? (
                          <Input
                            type="number"
                            value={tarifPoidsForm.ouvrable.minimumHt}
                            onChange={(e) => setTarifPoidsForm((f) => ({ ...f, ouvrable: { ...f.ouvrable, minimumHt: e.target.value } }))}
                            className="w-32 text-right ml-auto"
                          />
                        ) : (
                          <span className="font-mono">{tarifsData?.tarifPoids?.ouvrable?.minimumHt?.toLocaleString('fr-FR') ?? '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingTarifPoids ? (
                          <Input
                            type="number"
                            value={tarifPoidsForm.ouvrable.rateHtPerTon}
                            onChange={(e) => setTarifPoidsForm((f) => ({ ...f, ouvrable: { ...f.ouvrable, rateHtPerTon: e.target.value } }))}
                            className="w-32 text-right ml-auto"
                          />
                        ) : (
                          <span className="font-mono">{tarifsData?.tarifPoids?.ouvrable?.rateHtPerTon?.toLocaleString('fr-FR') ?? '—'}</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-700">{t('systemConfig.tariffs.special', 'Jours spéciaux / fériés')}</td>
                      <td className="px-4 py-3 text-right">
                        {editingTarifPoids ? (
                          <Input
                            type="number"
                            value={tarifPoidsForm.special.minimumHt}
                            onChange={(e) => setTarifPoidsForm((f) => ({ ...f, special: { ...f.special, minimumHt: e.target.value } }))}
                            className="w-32 text-right ml-auto"
                          />
                        ) : (
                          <span className="font-mono">{tarifsData?.tarifPoids?.special?.minimumHt?.toLocaleString('fr-FR') ?? '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingTarifPoids ? (
                          <Input
                            type="number"
                            value={tarifPoidsForm.special.rateHtPerTon}
                            onChange={(e) => setTarifPoidsForm((f) => ({ ...f, special: { ...f.special, rateHtPerTon: e.target.value } }))}
                            className="w-32 text-right ml-auto"
                          />
                        ) : (
                          <span className="font-mono">{tarifsData?.tarifPoids?.special?.rateHtPerTon?.toLocaleString('fr-FR') ?? '—'}</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {editingTarifPoids && (
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="outline" onClick={() => setEditingTarifPoids(false)} disabled={savingTarifs}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleSaveTarifPoids} loading={savingTarifs} disabled={savingTarifs}>
                    {t('common.save')}
                  </Button>
                </div>
              )}
            </div>

            {/* TVA Rate */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t('systemConfig.tariffs.tvaTitle', 'Taux TVA')}
                </h3>
                {!editingTvaRate && (
                  <Button variant="outline" size="sm" onClick={() => setEditingTvaRate(true)} disabled={savingTarifs}>
                    <PencilIcon className="h-4 w-4 mr-1" />
                    {t('common.edit')}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-4">
                {editingTvaRate ? (
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={tvaRateForm}
                      onChange={(e) => setTvaRateForm(e.target.value)}
                      className="w-32"
                    />
                    <span className="text-sm text-gray-600">
                      {t('systemConfig.tariffs.tvaHint', '(ex: 0.18 = 18%)')}
                    </span>
                    <Button variant="outline" onClick={() => setEditingTvaRate(false)} disabled={savingTarifs}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSaveTvaRate} loading={savingTarifs} disabled={savingTarifs}>
                      {t('common.save')}
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">
                    <span className="font-mono text-base font-semibold">{tarifsData?.tvaRate ?? '—'}</span>
                    <span className="ml-2 text-gray-500">
                      ({tarifsData?.tvaRate != null ? `${(tarifsData.tvaRate * 100).toFixed(0)}%` : '—'})
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Taxes Supplementaires (TS) */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t('systemConfig.tariffs.tsTitle', 'Taxes Supplémentaires (TS) — Tarifs horaires nets')}
                </h3>
                {!editingTsOptions && (
                  <Button variant="outline" size="sm" onClick={() => setEditingTsOptions(true)} disabled={savingTarifs}>
                    <PencilIcon className="h-4 w-4 mr-1" />
                    {t('common.edit')}
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">{t('systemConfig.tariffs.tsCode', 'Code')}</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">{t('systemConfig.tariffs.tsDescription', 'Description')}</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-700">{t('systemConfig.tariffs.tsHourlyRate', 'Tarif horaire net (FCFA/h)')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(editingTsOptions ? tsOptionsForm : tarifsData?.tsOptions || []).map((opt, idx) => (
                      <tr key={opt.code}>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{opt.code}</td>
                        <td className="px-4 py-3">
                          {editingTsOptions ? (
                            <Input
                              value={tsOptionsForm[idx]?.description || ''}
                              onChange={(e) => setTsOptionsForm((arr) => arr.map((o, i) => i === idx ? { ...o, description: e.target.value } : o))}
                            />
                          ) : (
                            opt.description
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {editingTsOptions ? (
                            <Input
                              type="number"
                              value={tsOptionsForm[idx]?.hourlyNetAmount ?? ''}
                              onChange={(e) => setTsOptionsForm((arr) => arr.map((o, i) => i === idx ? { ...o, hourlyNetAmount: e.target.value } : o))}
                              className="w-32 text-right ml-auto"
                            />
                          ) : (
                            <span className="font-mono">{opt.hourlyNetAmount?.toLocaleString('fr-FR') ?? '—'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {editingTsOptions && (
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="outline" onClick={() => setEditingTsOptions(false)} disabled={savingTarifs}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleSaveTsOptions} loading={savingTarifs} disabled={savingTarifs}>
                    {t('common.save')}
                  </Button>
                </div>
              )}
            </div>

              </div>
            )}
          </>
        )}
      </div>

      {/* Configuration Groups */}
      {Object.keys(groupedConfigs).length === 0 ? (
        <div className="card text-center py-12">
          <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('systemConfig.noConfigs')}</h3>
          <p className="text-gray-500 mb-4">{t('systemConfig.noConfigsDescription')}</p>
          <Button onClick={() => setShowAddModal(true)}>
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('systemConfig.addFirstConfig')}
          </Button>
        </div>
      ) : (
        Object.keys(groupedConfigs).map(category => (
          <div key={category} className="card">
            <button
              type="button"
              onClick={() => toggleSection(`category:${category}`)}
              className="w-full flex items-center justify-between gap-3 text-left mb-4"
            >
              <h2 className="text-lg font-semibold text-gray-900 capitalize">
                {category === 'module' ? t('systemConfig.modules') : category}
              </h2>
              {isSectionExpanded(`category:${category}`) ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500 shrink-0" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500 shrink-0" />
              )}
            </button>
            {isSectionExpanded(`category:${category}`) && (
              <div className="space-y-4">
              {groupedConfigs[category].map(config => {
                const isEditing = editingKey === config.key;
                const isBoolean = config.type === 'boolean';
                const isEnabled = isBoolean && (config.value === true || config.value === 'true' || config.value === '1');

                return (
                  <div
                    key={config.key}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-base font-medium text-gray-900">
                            {config.label || config.key}
                          </h3>
                          {isBoolean && (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                isEnabled
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {isEnabled ? (
                                <>
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  {t('systemConfig.enabled')}
                                </>
                              ) : (
                                <>
                                  <XCircleIcon className="h-3 w-3 mr-1" />
                                  {t('systemConfig.disabled')}
                                </>
                              )}
                            </span>
                          )}
                        </div>
                        {config.description && (
                          <p className="text-sm text-gray-600 mb-2">{config.description}</p>
                        )}
                        <div className="text-xs text-gray-500">
                          <span className="font-mono">{config.key}</span>
                          {config.updatedBy && config.updater && (
                            <span className="ml-4">
                              {t('systemConfig.lastUpdatedBy')}: {config.updater.firstName} {config.updater.lastName}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {isBoolean ? (
                          <Button
                            variant={isEnabled ? 'danger' : 'success'}
                            size="sm"
                            onClick={() => handleToggle(config)}
                            disabled={togglingKey === config.key}
                            loading={togglingKey === config.key}
                          >
                            {isEnabled ? (
                              <>
                                <XCircleIcon className="h-4 w-4 mr-1" />
                                {t('systemConfig.disable')}
                              </>
                            ) : (
                              <>
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                {t('systemConfig.enable')}
                              </>
                            )}
                          </Button>
                        ) : (
                          <>
                            {!isEditing && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(config)}
                              >
                                <PencilIcon className="h-4 w-4 mr-1" />
                                {t('common.edit')}
                              </Button>
                            )}
                          </>
                        )}
                        {!isBoolean && !isEditing && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(config.key)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Edit Form for non-boolean configs */}
                    {isEditing && !isBoolean && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                        <Input
                          label={t('systemConfig.value')}
                          type={config.type === 'number' ? 'number' : 'text'}
                          value={editForm.value || ''}
                          onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                        />
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" onClick={handleCancel}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={() => handleSave(config.key)}>
                            {t('common.save')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            )}
          </div>
        ))
      )}

      {/* Add Config Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('systemConfig.addConfig')}</h2>
              
              <div className="space-y-4">
                <Input
                  label={t('systemConfig.key')}
                  value={newConfig.key}
                  onChange={(e) => setNewConfig({ ...newConfig, key: e.target.value })}
                  placeholder="module.tracking.enabled"
                  required
                />
                <Input
                  label={t('systemConfig.label')}
                  value={newConfig.label}
                  onChange={(e) => setNewConfig({ ...newConfig, label: e.target.value })}
                  placeholder={t('systemConfig.labelPlaceholder')}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('systemConfig.type')}
                  </label>
                  <select
                    value={newConfig.type}
                    onChange={(e) => setNewConfig({ ...newConfig, type: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="boolean">{t('systemConfig.types.boolean')}</option>
                    <option value="string">{t('systemConfig.types.string')}</option>
                    <option value="number">{t('systemConfig.types.number')}</option>
                    <option value="json">{t('systemConfig.types.json')}</option>
                  </select>
                </div>
                <Input
                  label={t('systemConfig.value')}
                  type={newConfig.type === 'number' ? 'number' : 'text'}
                  value={newConfig.value}
                  onChange={(e) => setNewConfig({ ...newConfig, value: e.target.value })}
                  placeholder={newConfig.type === 'boolean' ? t('systemConfig.booleanValuePlaceholder') : t('systemConfig.valuePlaceholder')}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('systemConfig.category')}
                  </label>
                  <select
                    value={newConfig.category}
                    onChange={(e) => setNewConfig({ ...newConfig, category: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="module">{t('systemConfig.categories.module')}</option>
                    <option value="feature">{t('systemConfig.categories.feature')}</option>
                    <option value="setting">{t('systemConfig.categories.setting')}</option>
                    <option value="other">{t('systemConfig.categories.other')}</option>
                  </select>
                </div>
                <Input
                  label={t('systemConfig.description')}
                  value={newConfig.description}
                  onChange={(e) => setNewConfig({ ...newConfig, description: e.target.value })}
                  placeholder={t('systemConfig.descriptionPlaceholder')}
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleAdd}>
                  {t('common.add')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemConfigPage;


